import { create } from 'zustand';
import { createJSONStorage, persist } from 'zustand/middleware';
import type { HelpGuideId } from '@/features/helpGuides/types';

type GuideProgressByUser = Record<string, HelpGuideId[]>;
type LastStartedByUser = Record<string, HelpGuideId | null>;

interface HelpGuidesState {
  isHelpGuideOpen: boolean;
  pendingGuideToStart: HelpGuideId | null;
  pendingGuideRequestId: number;
  completedGuideIdsByUser: GuideProgressByUser;
  lastStartedGuideByUser: LastStartedByUser;
  openHelpGuide: () => void;
  closeHelpGuide: () => void;
  setHelpGuideOpen: (open: boolean) => void;
  startGuide: (userId: string, guideId: HelpGuideId) => void;
  clearPendingGuide: () => void;
  markGuideCompleted: (userId: string, guideId: HelpGuideId) => void;
  getCompletedGuideIds: (userId: string) => HelpGuideId[];
  isGuideCompleted: (userId: string, guideId: HelpGuideId) => boolean;
}

export const useHelpGuidesStore = create<HelpGuidesState>()(
  persist(
    (set, get) => ({
      isHelpGuideOpen: false,
      pendingGuideToStart: null,
      pendingGuideRequestId: 0,
      completedGuideIdsByUser: {},
      lastStartedGuideByUser: {},

      openHelpGuide: () => set({ isHelpGuideOpen: true }),
      closeHelpGuide: () => set({ isHelpGuideOpen: false }),
      setHelpGuideOpen: (open) => set({ isHelpGuideOpen: open }),

      startGuide: (userId, guideId) => {
        set((state) => ({
          pendingGuideToStart: guideId,
          pendingGuideRequestId: state.pendingGuideRequestId + 1,
          lastStartedGuideByUser: {
            ...state.lastStartedGuideByUser,
            [userId]: guideId,
          },
        }));
      },

      clearPendingGuide: () => set({ pendingGuideToStart: null }),

      markGuideCompleted: (userId, guideId) => {
        set((state) => {
          const current = state.completedGuideIdsByUser[userId] || [];
          if (current.includes(guideId)) {
            return state;
          }

          return {
            completedGuideIdsByUser: {
              ...state.completedGuideIdsByUser,
              [userId]: [...current, guideId],
            },
          };
        });
      },

      getCompletedGuideIds: (userId) => get().completedGuideIdsByUser[userId] || [],

      isGuideCompleted: (userId, guideId) => {
        const completed = get().completedGuideIdsByUser[userId] || [];
        return completed.includes(guideId);
      },
    }),
    {
      name: 'help-guides-storage',
      storage: createJSONStorage(() => localStorage),
      partialize: (state) => ({
        completedGuideIdsByUser: state.completedGuideIdsByUser,
        lastStartedGuideByUser: state.lastStartedGuideByUser,
      }),
    },
  ),
);
